// src/pages/cadastros/Setores/EditarSetor.jsx
// (Mamute Fitness) UI rebatizado para "Academias".
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useIsMobile } from "../../../hooks/useIsMobile";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import GoogleMapsAutocomplete from "../../../components/GoogleMapsAutocomplete/GoogleMapsAutocomplete";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { useAuth } from "../../../contexts/AuthContext";

function EditarSetor() {
  const { id } = useParams();
  const { tema } = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { usuario: usuarioLogado } = useAuth();

  const [sidebarAberta, setSidebarAberta] = useState(false);

  const grupoId = usuarioLogado?.grupoId || null;

  const [empresas, setEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState("");

  const [nome, setNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [contato, setContato] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [quantidadeEquipamentos, setQuantidadeEquipamentos] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [carregando, setCarregando] = useState(true);
  const [carregandoEmpresas, setCarregandoEmpresas] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [alerta, setAlerta] = useState({ tipo: "", msg: "" });

  const titulo = useMemo(() => "Editar Academia", []);

  function toInputDate(v) {
    if (!v) return "";
    const d = typeof v?.toDate === "function" ? v.toDate() : v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }


  useEffect(() => {
    async function carregarAcademia() {
      if (!id) return;

      setCarregando(true);
      setAlerta({ tipo: "", msg: "" });

      try {
        const ref = doc(db, "setores", id);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setAlerta({ tipo: "erro", msg: "Academia não encontrada." });
          return;
        }

        const data = snap.data() || {};
        setEmpresaSelecionada(data.empresaId || "");
        setNome(data.nome || "");
        setEndereco(data.endereco || "");
        setContato(data.contato || data.responsavel || "");
        setTelefone(data.telefone || "");
        setEmail(data.email || "");
          const qtd = data.quantidadeEquipamentos ?? data.qtdEquipamentos ?? "";
          setQuantidadeEquipamentos(qtd === null || qtd === undefined ? "" : String(qtd));
        setObservacoes(data.observacoes || data.obs || "");
      } catch (e) {
        console.error("Erro ao carregar academia:", e);
        setAlerta({ tipo: "erro", msg: "Erro ao carregar academia. Tente novamente." });
      } finally {
        setCarregando(false);
      }
    }

    carregarAcademia();
  }, [id]);

  useEffect(() => {
    async function carregarEmpresas() {
      if (!grupoId) {
        setCarregandoEmpresas(false);
        return;
      }

      setCarregandoEmpresas(true);

      try {
        const empresasRef = collection(db, "empresas");
        const q = query(empresasRef, where("grupoId", "==", grupoId));
        const snap = await getDocs(q);

        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));

        setEmpresas(lista);
      } catch (e) {
        console.error("Erro ao carregar clientes (empresas):", e);
        setAlerta({ tipo: "erro", msg: "Erro ao carregar clientes. Tente novamente." });
      } finally {
        setCarregandoEmpresas(false);
      }
    }

    carregarEmpresas();
  }, [grupoId]);

  async function onSubmit(e) {
    e.preventDefault();
    setAlerta({ tipo: "", msg: "" });

    if (!grupoId) {
      setAlerta({ tipo: "erro", msg: "Usuário sem grupoId. Verifique seu cadastro." });
      return;
    }

    if (!empresaSelecionada) {
      setAlerta({ tipo: "erro", msg: "Selecione o cliente." });
      return;
    }

    if (!nome.trim()) {
      setAlerta({ tipo: "erro", msg: "Informe o nome da academia." });
      return;
    }

    setSalvando(true);

    try {
      const empresaData = empresas.find((e) => e.id === empresaSelecionada) || null;

      const qtd = String(quantidadeEquipamentos || "").trim();
      const qtdNum = qtd ? Number(qtd) : null;

      const ref = doc(db, "setores", id);

      await updateDoc(ref, {
        // escopo
        grupoId,
        empresaId: empresaSelecionada,
        empresaNome: empresaData?.nome || empresaData?.empresaNome || "",
        empresaCodigo: empresaData?.codigo || empresaData?.empresaCodigo || "",

        // dados
        nome: nome.trim(),
        endereco: endereco.trim(),
        contato: contato.trim(),
        telefone: telefone.trim(),
        email: email.trim(),
        quantidadeEquipamentos: Number.isFinite(qtdNum) ? qtdNum : null,
        observacoes: observacoes.trim(),

        // auditoria
        updatedAt: serverTimestamp(),
      });

      setAlerta({ tipo: "ok", msg: "Academia atualizada com sucesso." });
    } catch (e) {
      console.error("Erro ao atualizar academia:", e);
      setAlerta({ tipo: "erro", msg: "Erro ao atualizar academia. Tente novamente." });
    } finally {
      setSalvando(false);
    }
  }

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  const inputStyle = {
    width: "100%",
    padding: "9px 11px",
    marginTop: 4,
    borderRadius: 8,
    border: "1px solid #334155",
    boxSizing: "border-box",
  };

  const conteudo = (
    <section style={{ width: "100%" }}>
      <div
        className={isMobile ? "m-card" : "pc-card"}
        style={{
          width: "100%",
          padding: isMobile ? 16 : "16px 40px",
          boxSizing: "border-box",
        }}
      >
        {!isMobile && (
          <header className="pc-table-header">
            <div className="pc-table-header-text">
              <h3>Dados da academia</h3>
              <p>Atualize os dados da academia.</p>
            </div>
          </header>
        )}

        {alerta?.msg && (
          <div
            style={{
              marginTop: isMobile ? 0 : 12,
              marginBottom: 12,
              padding: 8,
              borderRadius: 8,
              background: alerta.tipo === "ok" ? "#14532d" : "#7f1d1d",
              color: alerta.tipo === "ok" ? "#bbf7d0" : "#fecaca",
              fontSize: 14,
            }}
          >
            {alerta.msg}
          </div>
        )}

        {carregando ? (
          <p style={{ padding: 10 }}>Carregando...</p>
        ) : (
          <form
            onSubmit={onSubmit}
            style={{
              marginTop: isMobile ? 0 : 12,
              maxWidth: isMobile ? "100%" : 720,
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* CLIENTE */}
            <label>
              <span style={{ fontSize: 14 }}>Cliente</span>
              <select
                value={empresaSelecionada}
                onChange={(e) => setEmpresaSelecionada(e.target.value)}
                disabled={carregandoEmpresas}
                className={isMobile ? "m-select" : ""}
                style={{ ...inputStyle, color: "inherit" }}
              >
                <option value="">
                  {carregandoEmpresas ? "Carregando..." : "Selecione o cliente"}
                </option>
                {empresas.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

            {/* NOME */}
            <label>
              <span style={{ fontSize: 14 }}>Nome da academia</span>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={inputStyle}
              />
            </label>

            {/* ENDEREÇO */}
            <label>
              <span style={{ fontSize: 14 }}>Endereço</span>
              <GoogleMapsAutocomplete
                value={endereco}
                onChange={setEndereco}
                onPlaceSelected={(place) => setEndereco(place.address || "")}
                className={isMobile ? "m-input" : ""}
                inputStyle={inputStyle}
                grupoId={grupoId || ""}
                disabled={!grupoId}
              />
            </label>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 12,
              }}
            >
              {/* CONTATO */}
              <label>
                <span style={{ fontSize: 14 }}>Contato</span>
                <input
                  type="text"
                  value={contato}
                  onChange={(e) => setContato(e.target.value)}
                  className={isMobile ? "m-input" : ""}
                  style={inputStyle}
                />
              </label>

              {/* TELEFONE */}
              <label>
                <span style={{ fontSize: 14 }}>Telefone</span>
                <input
                  type="tel"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  className={isMobile ? "m-input" : ""}
                  style={inputStyle}
                />
              </label>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 12,
              }}
            >
              {/* EMAIL */}
              <label>
                <span style={{ fontSize: 14 }}>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={isMobile ? "m-input" : ""}
                  style={inputStyle}
                />
              </label>

              {/* QTD EQUIP */}
              <label>
                <span style={{ fontSize: 14 }}>Quantidade de equipamentos</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={quantidadeEquipamentos}
                  onChange={(e) => setQuantidadeEquipamentos(e.target.value)}
                  className={isMobile ? "m-input" : ""}
                  style={inputStyle}
                />
              </label>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 12,
              }}
            >
            </div>

            {/* OBS */}
            <label>
              <span style={{ fontSize: 14 }}>Observações</span>
              <textarea
                rows={4}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                className={isMobile ? "m-input" : ""}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </label>

            <div
              className={isMobile ? "m-form-actions" : ""}
              style={
                isMobile
                  ? {}
                  : {
                      marginTop: 16,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                    }
              }
            >
              <button
                type="submit"
                className={isMobile ? "m-btn-primary" : "pc-btn-primario"}
                disabled={salvando || carregandoEmpresas || carregando}
              >
                {salvando ? "Salvando..." : "Salvar alterações"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/cadastros/setores")}
                className={isMobile ? "m-btn-secondary" : "pc-btn-secundario"}
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );

  return (
    <Layout
      tema={tema}
      titulo={titulo}
      subtitulo="Cadastros / Academias / Editar"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
    >
      {conteudo}
    </Layout>
  );
}

export default EditarSetor;
